import { createMemo, createResource, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { AlertCircle, Grid3x3, Loader2 } from "lucide-solid";

import { pressable, staggerItem } from "@/lib/motion";
import { useI18n } from "@/providers/i18n-provider";
import { fetchCatalogProducts, type CatalogProductDTO } from "../data/catalog-rest";
import { ProductCard } from "./product-card";

const ALL = "__all__";

/**
 * 🎯 Catálogo global (guest-first). Lee el dataset público por REST
 * (`/api/catalog/products`), así funciona idéntico con o sin cuenta. Grid
 * responsivo + filtro por categoría (chips). Traducción del diseño Stitch
 * `cat_logo_principal` a SolidJS + tokens Forest Nurture.
 */
export function CatalogPage() {
  const { t } = useI18n();
  const [products] = createResource<CatalogProductDTO[]>(async () => {
    const controller = new AbortController();
    return fetchCatalogProducts({ signal: controller.signal });
  });
  const [category, setCategory] = createSignal<string>(ALL);

  // Categorías presentes en el dataset (para los chips de filtro).
  const categories = createMemo(() => {
    const set = new Set<string>();
    for (const p of products() ?? []) {
      if (p.category) {
        set.add(p.category);
      }
    }
    return [...set].toSorted((a, b) => a.localeCompare(b));
  });

  const visible = createMemo(() => {
    const cat = category();
    const list = products() ?? [];
    return cat === ALL ? list : list.filter((p) => p.category === cat);
  });

  const countLabel = () => {
    const shown = visible().length;
    const total = (products() ?? []).length;
    return t(msg`Mostrando ${shown} de ${total} sets`);
  };

  return (
    <div class="flex flex-col gap-6" data-testid="catalog-page">
      <header>
        <h1 class="text-2xl font-semibold text-foreground">{t(msg`Catálogo Sylvanian`)}</h1>
        <p class="mt-1 text-sm text-muted-foreground">
          {t(msg`Descubre cada set y añade los tuyos a la colección.`)}
        </p>
      </header>

      {/* Chips de filtro por categoría */}
      <Show when={categories().length > 0}>
        <div class="flex flex-wrap gap-2" role="group" aria-label={t(msg`Filtrar por categoría`)}>
          <FilterChip
            active={category() === ALL}
            label={t(msg`Todos`)}
            onClick={() => setCategory(ALL)}
          />
          <For each={categories()}>
            {(cat) => (
              <FilterChip
                active={category() === cat}
                label={cat}
                onClick={() => setCategory(cat)}
              />
            )}
          </For>
        </div>
      </Show>

      <Show
        when={!products.loading}
        fallback={
          <div class="grid place-items-center py-20 text-muted-foreground">
            <Loader2 class="h-8 w-8 animate-spin" />
          </div>
        }
      >
        <Show
          when={!products.error}
          fallback={
            <div class="grid place-items-center gap-2 py-20 text-center text-muted-foreground">
              <AlertCircle class="h-8 w-8" />
              <p class="text-sm">{t(msg`No se pudo cargar el catálogo. Revisa tu conexión.`)}</p>
            </div>
          }
        >
          <Show
            when={visible().length > 0}
            fallback={
              <div class="grid place-items-center gap-2 py-20 text-center text-muted-foreground">
                <Grid3x3 class="h-8 w-8" />
                <p class="text-sm">{t(msg`No hay productos en esta categoría todavía.`)}</p>
              </div>
            }
          >
            <section class="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {/* Entrada escalonada GPU-only (lib/motion): las tarjetas
                  cascadean al montar la lista o cambiar de filtro. */}
              <For each={visible()}>
                {(product, index) => (
                  <div ref={(el: HTMLDivElement) => staggerItem(el, index())} class="flex">
                    <ProductCard product={product} />
                  </div>
                )}
              </For>
            </section>
            <p class="text-center text-xs italic text-muted-foreground">{countLabel()}</p>
          </Show>
        </Show>
      </Show>
    </div>
  );
}

function FilterChip(props: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={() => props.onClick()}
      aria-pressed={props.active}
      ref={(el: HTMLButtonElement) => pressable(el, { scale: 0.94 })}
      class={
        "rounded-full px-4 py-1.5 text-sm font-medium transition-colors " +
        (props.active
          ? "bg-primary text-primary-foreground shadow-sm"
          : // Inactivo = píldora Meadow Pastel (--default) con texto bark, como el
            // diseño Stitch. OJO: NO bg-accent — accent es el mismo forest que el
            // activo y dejaba todos los chips idénticos.
            "bg-default text-default-foreground hover:bg-default/70")
      }
    >
      {props.label}
    </button>
  );
}
